// apps/share-web/src/text-share/live-badge.ts
//
// Кнопка Live в шапке страницы слов: четыре вида и одно действие.
//
// | состояние            | вид                        | что говорит         |
// |----------------------|----------------------------|---------------------|
// | лайва ещё не было    | приглушённая, нажать нечего| «ещё не начался»    |
// | идёт, не подключены  | золотая, точка пульсирует  | «подключиться»      |
// | идёт, подключены     | зелёная точка              | «подключено»        |
// | якорь протух         | КРАСНАЯ                    | «нет связи»         |
//
// ⚠️ ПРОТУХШЕЕ СОСТОЯНИЕ КРАСНОЕ, А НЕ ОРАНЖЕВОЕ, и цвет здесь надо называть
// правильно. До 21.09 шапка (и соседи в live-follow.ts и live-model.ts) звала
// его оранжевым, тогда как text-share.css с самого первого коммита красит и
// точку, и подпись в `var(--red)`. Цена вышла не косметическая: разбор
// B-260912 начался с вывода «Сергей описал КРАСНЫЙ экран, а кнопка оранжевая,
// значит он видел не кнопку, а страничный экран ошибки поверх всего» — то есть
// расхождение комментария с таблицей стилей отправило разбор искать
// несуществующий оверлей. Красный это и был. Меняешь тон — правь обе стороны.
//
// КРАСНОЕ НЕ ОБЕЩАЕТ «СЧИТАЮ САМ», и это главное решение модуля. Соблазн
// написать так велик: слой 2 архитектуры §6a ровно об этом — лист продолжает
// вестись по расписанию, пока сеть молчит. Но `stale` покрывает ДВА периода:
// пока модель ещё выводит позицию и после того, как она сдалась по потолку
// LIVE_GIVE_UP_MS (десять минут молчания = лидер ушёл, а не «отключился»).
// Обещание, верное в первой половине состояния и ложное во второй, — тот же
// класс, что стоил волне B двух коммитов на невыполненные обещания. Ничего не
// обещаем: лист либо движется, и это видно, либо нет.
//
// `ended` и `gone` — состояния СТРАНИЦЫ, а не кнопки: у завершённой ссылки своя
// страница (волна B), у 404 — своя. Кнопка в этих двух молчит, чтобы не спорить
// с ними вторым голосом.
//
// ПОЧЕМУ НЕ ДС-КОМПОНЕНТ. `<bw-tag>` — не кнопка вовсе, и его тонов три
// (default | amber | red, bw-tag.ts:10): зелёного, то есть ровно того, что
// отличает «подключено» от «предлагаю подключиться», у него нет. `<bw-button>`
// — кнопка, но красить её из потребителя запрещено правилом самого модуля
// кнопок («классы потребителя кнопку СТАВЯТ, а не красят», chart-buttons.ts:69),
// а цвет здесь и есть содержание. Поэтому это своя `<button>` — тем же
// исключением и по той же причине, что ряд песни `.ts-song` в words-view.ts.

import type { LinkState } from '../live/live-model.js';
import { t } from '../ui/i18n.js';

export type BadgeTone = 'idle' | 'offer' | 'connected' | 'stale';

export interface BadgeView {
  /** Кнопки не видно вовсе: за неё говорит страница (см. шапку). */
  hidden: boolean;
  tone: BadgeTone;
  labelKey: string;
  /** Пульс точки — приглашение, а не индикатор: он только у «подключиться». */
  pulsing: boolean;
  /** Нажимать нечего: сессии нет. */
  disabled: boolean;
}

/** Состояние связи + наше решение → вид кнопки. Чистая функция. */
export function badgeView(state: LinkState, connected: boolean): BadgeView {
  switch (state) {
    case 'ended':
    case 'gone':
      return { hidden: true, tone: 'idle', labelKey: 'textShare.liveIdle', pulsing: false, disabled: true };
    case 'stale':
      return { hidden: false, tone: 'stale', labelKey: 'textShare.liveStale', pulsing: false, disabled: false };
    case 'live':
      return connected
        ? { hidden: false, tone: 'connected', labelKey: 'textShare.liveConnected', pulsing: false, disabled: false }
        : { hidden: false, tone: 'offer', labelKey: 'textShare.liveOffer', pulsing: true, disabled: false };
    case 'idle':
      return { hidden: false, tone: 'idle', labelKey: 'textShare.liveIdle', pulsing: false, disabled: true };
  }
}

export interface LiveBadgeHandle {
  /**
   * Сам узел. Страница им ВЛАДЕЕТ и переставляет: пока открыт слой чтения,
   * кнопка живёт в его первом ряду, иначе — в шапке страницы. Один узел на обе
   * поверхности, а не два состояния в двух местах.
   */
  readonly el: HTMLElement;
  sync(state: LinkState, connected: boolean): void;
  destroy(): void;
}

export function createLiveBadge(onToggle: () => void): LiveBadgeHandle {
  const btn = document.createElement('button');
  btn.className = 'ts-live';
  btn.type = 'button';

  const dot = document.createElement('span');
  dot.className = 'ts-live-dot';
  // Точка декоративна: всё, что она значит, сказано подписью рядом.
  dot.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'ts-live-label';
  btn.appendChild(dot);
  btn.appendChild(label);

  btn.addEventListener('click', () => onToggle());

  let last = '';

  return {
    el: btn,
    sync(state, connected) {
      const view = badgeView(state, connected);
      // Импульс страницы приходит несколько раз в секунду, а вид меняется раз в
      // минуты. Перерисовка на каждый импульс сбрасывала бы CSS-анимацию точки
      // и трогала бы DOM без причины.
      const key = `${view.tone}|${view.hidden}|${view.pulsing}|${view.disabled}`;
      if (key === last) return;
      last = key;
      btn.dataset.tone = view.tone;
      btn.hidden = view.hidden;
      btn.disabled = view.disabled;
      btn.dataset.pulsing = view.pulsing ? 'yes' : 'no';
      const text = t(view.labelKey);
      label.textContent = text;
      // В слое чтения подпись спрятана от глаза (text-share.css): состояние там
      // несёт цвет точки. `title` возвращает слово курсору, а скринридеру его
      // отдаёт сама подпись — она из дерева доступности не удаляется.
      btn.title = text;
    },
    destroy() { btn.remove(); },
  };
}
